import paymentGetters from '../../getters/paymentGetters';
import shippingMethodGetters from '../../getters/shippingMethodGetters';
import { CheckoutNavigationStatus } from '~/composables/custom/useCheckoutNavigation/useCheckoutNavigation';
import { useCart } from '~/composables/custom/useCart/useCart';
import { useShippingProvider } from '~/composables/custom/useShippingProvider';
import { usePayment } from '~/composables/custom/usePayment/usePayment';
import type { PaymentMethod } from '~/composables/custom/usePayment/usePayment';
import type { ShippingMethod } from '~/types/GraphQL';

const selectedPaymentOption = ref({} as PaymentMethod);
const isValidDate = ref(true);
export const usePaymentNavigation = () => {
  const {
    updating, loading: cartLoading, cart,
  } = useCart();
  const {
    loading: loadingPayment, errors: paymentErrors, resetError: resetPaymentErrors,
  } = usePayment();

  const {
    shippingMethodsByCart: shippingMethods,
  } = useShippingProvider();

  const { methods: paymentMethods } = useRuntimeConfig().public.payment;
  const { $domain } = useNuxtApp();

  const updatesOnPaymentAllowed = computed(() => !updating.value && !loadingPayment.value);

  const canProceedAfterPayment = computed(() => {
    const noPaymentErrors = !Object.values(paymentErrors.value).filter(Boolean).length;
    return noPaymentErrors && !cartLoading.value && selectedPaymentOption.value.provider !== undefined;
  });

  const hasMissingAddresses = computed(() => {
    const isCompany = cart.value?.billingAddress?.custom?.customFieldsRaw?.find(({ name, value }) => name === 'addressType' && value === 'company');
    if ($domain.countryCode.toUpperCase() === 'PL' && isCompany) {
      return false; // for PL we don't require first name when adress is of type company
    }

    return !cart.value?.shippingAddress?.firstName || !cart.value?.billingAddress?.firstName;
  });

  const selectedShippingMethod = computed(() => shippingMethods.value?.find((method: ShippingMethod) => method.id === cart.value?.shippingInfo?.shippingMethod?.id));
  const shouldRestart = computed(() => (
    hasMissingAddresses.value
    || (shippingMethodGetters.isClickAndCollect(selectedShippingMethod.value) && !shippingMethodGetters.isShippingAvailable(selectedShippingMethod.value, cart.value))
  ));

  const hasMissingMandatoryInfoForUnzer = computed(() => {
    const activePaymentMethod = paymentGetters.getActivePaymentMethod(cart.value?.paymentInfo);
    if (activePaymentMethod?.paymentMethodInfo.method !== paymentMethods.INVOICE) {
      return false;
    }

    if (cart.value?.customer) {
      return !cart.value.customer.dateOfBirth;
    }

    return !cart.value?.billingAddress?.custom?.customFieldsRaw?.some((field: any) => field.name === 'dateOfBirth');
  });

  const missingCartInfo = computed(() => !cart.value?.paymentInfo || hasMissingMandatoryInfoForUnzer.value);

  const nextState = computed(() => {
    if (!updatesOnPaymentAllowed.value) {
      return CheckoutNavigationStatus.LOADING;
    }

    if (!cart.value || shouldRestart.value || !canProceedAfterPayment.value || missingCartInfo.value) {
      return CheckoutNavigationStatus.MISSING_INFO;
    }

    const { method } = selectedPaymentOption.value || {};
    if (![paymentMethods.FILIALE, paymentMethods.VORKASSE, paymentMethods.INVOICE].includes(method)) {
      return CheckoutNavigationStatus.TO_PAYMENT;
    }

    const isInvoiceReady = !hasMissingMandatoryInfoForUnzer.value && isValidDate.value;
    const otherPaymentMethods = method && [paymentMethods.FILIALE, paymentMethods.VORKASSE].includes(method);
    if (isInvoiceReady || otherPaymentMethods) {
      return CheckoutNavigationStatus.TO_ORDER;
    }

    return CheckoutNavigationStatus.MISSING_INFO;
  });

  const resetErrors = () => {
    resetPaymentErrors('loadPayments');
    resetPaymentErrors('addPaymentOption');
    resetPaymentErrors('createPaymentOrder');
  };

  const setPaymentMethod = (paymentOption: PaymentMethod) => {
    if (paymentOption
      && selectedPaymentOption.value?.method === paymentOption?.method
      && selectedPaymentOption.value?.provider === paymentOption?.provider
    ) {
      return;
    }

    resetErrors();

    selectedPaymentOption.value = paymentOption;
  };
  const setIsValidDate = (valid: boolean) => {
    isValidDate.value = valid;
  };
  return {
    shouldRestart,
    setPaymentMethod,
    setIsValidDate,
    paymentOption: computed(() => selectedPaymentOption.value),
    nextState: computed(() => nextState.value),
  };
};
